import React, {useEffect, useState} from "react";
import {CardElement, Elements, PaymentRequestButtonElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {useStripePayment} from "../../../hooks/useStripePayment";
import {Button} from "../../common/Button";
import {InfoBox} from "../../common/InfoBox";
import {useFunnel} from "../../../context/FunnelContext";
import {getStripeConfig} from "../../../config/stripe";
import {trackFBEvent} from "../../../utils/facebookPixel";

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: "16px",
            color: "#424770",
            fontFamily: '"Inter", sans-serif',
            fontSmoothing: "antialiased",
            "::placeholder": {
                color: "#aab7c4",
            },
        },
        invalid: {
            color: "#dc2626",
            iconColor: "#dc2626",
        },
    },
    hidePostalCode: true,
    disableLink: true,
};

let stripePromise;

export function PaymentSection() {
    const {state} = useFunnel();
    const selectedPlan = state.userData.selectedPlan;

    return (
        <div className="max-w-md mx-auto mt-8 bg-white p-8 rounded-xl border border-gray-200 shadow-lg">
            <h4 className="text-2xl font-bold text-gray-800 text-center mb-8">
                Complete Your Purchase
            </h4>
            <StripeWrapper>
                <PaymentForm selectedPlanId={selectedPlan.stripe_id}/>
            </StripeWrapper>
        </div>
    );
}

function ApplePayButton({onPaymentMethodSelected}) {
    const stripe = useStripe();
    const elements = useElements();
    const [paymentRequest, setPaymentRequest] = useState(null);
    const {state} = useFunnel();

    useEffect(() => {
        if (!stripe || !elements || !state.userData.selectedPlan) return;

        const pr = stripe.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: {
                label: `${state.userData.selectedPlan.duration} Subscription`,
                amount: state.userData.selectedPlan.discountedPrice * 100, // Convert to cents
            },
            requestPayerEmail: true,
            requestPayerName: true,
        });

        pr.on('paymentmethod', async (event) => {
            onPaymentMethodSelected(event);
        });

        // Check if payment request is supported
        pr.canMakePayment().then(result => {
            if (result && (result.applePay || result.googlePay)) {
                setPaymentRequest(pr);
            }
        });
    }, [stripe, elements, state.userData.selectedPlan, onPaymentMethodSelected]);

    if (!paymentRequest) {
        return null;
    }

    return (
        <div className="mb-4">
            <PaymentRequestButtonElement
                options={{paymentRequest}}
                className="w-full"
            />
            <div className="text-center text-sm text-gray-500 mt-2">
                Or pay with card
            </div>
            <hr className="my-4"/>
        </div>
    );
}

function PaymentForm({selectedPlanId}) {
    const {processPayment, processApplePayment, loading, error} = useStripePayment();
    const {state} = useFunnel();

    const handleSubmit = async (event) => {
        event.preventDefault();

        trackFBEvent("AddPaymentInfo", {
            content_category: "Subscription",
            content_name: state.userData.selectedPlan.duration,
            currency: "USD",
            value: state.userData.selectedPlan.discountedPrice,
        });

        try {
            await processPayment(selectedPlanId);
        } catch (err) {
            console.error("Payment processing failed:", err);
        }
    };

    const handleApplePayment = async (event) => {
        try {
            await processApplePayment(event, selectedPlanId);
        } catch (err) {
            console.error("Apple Pay payment failed:", err);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                <ApplePayButton onPaymentMethodSelected={handleApplePayment}/>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg transition-all hover:border-gray-300">
                    <CardElement options={CARD_ELEMENT_OPTIONS}/>
                </div>
            </div>

            {error && (
                <InfoBox
                    variant="error"
                    icon="error"
                    title="Payment Error"
                    text={error}
                    className="animate-fadeIn"
                />
            )}

            <Button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-lg font-medium transition-all"
            >
                {loading ? (
                    <span className="flex items-center justify-center">
            <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
              <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
              ></circle>
              <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </span>
                ) : (
                    <span className="flex items-center justify-center gap-2">
            <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
            >
              <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4"
              />
            </svg>
            Buy Now
          </span>
                )}
            </Button>
        </form>
    );
}

function StripeWrapper({children}) {
    const [status, setStatus] = useState({
        loading: true,
        error: null,
    });

    useEffect(() => {
        const initializeStripe = async () => {
            try {
                if (!stripePromise) {
                    const {publishableKey} = getStripeConfig();
                    stripePromise = await loadStripe(publishableKey);
                }
                setStatus({loading: false, error: null});
            } catch (err) {
                setStatus({loading: false, error: err.message});
            }
        };

        initializeStripe();
    }, []);

    if (status.loading) {
        return (
            <div className="text-center py-6 text-gray-600">
                Initializing payment system...
            </div>
        );
    }

    if (status.error) {
        return (
            <div className="text-red-600 text-center py-6 bg-red-50 rounded-lg">
                Error: {status.error}
            </div>
        );
    }

    return <Elements stripe={stripePromise}>{children}</Elements>;
}
